import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ExpenseNotification {
  id: string;
  contractId: string;
  supplierName: string;
  amount: number;
  transactionDate: Date;
  description: string | null;
}

export interface PendingContractDetection {
  id: string;
  supplier_name: string;
  average_amount: number;
  currency_code: string;
  confidence_score: number;
  detected_at: Date;
  integration_id: string;
  erp_source: 'tripletex' | 'finago' | 'poweroffice' | 'business_central';
  client_name: string | null;
  detection_type: 'expense' | 'sales';
}

export function useExpenseNotifications() {
  const queryClient = useQueryClient();

  const fetchNewExpensesCount = async (): Promise<number> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    // Get last viewed timestamp
    const { data: prefs } = await supabase
      .from("user_preferences")
      .select("last_viewed_expenses_at")
      .eq("user_id", user.id)
      .single();

    if (!prefs?.last_viewed_expenses_at) return 0;

    // Count expenses created after last viewed
    const { count } = await supabase
      .from("contract_expense_timeline")
      .select("*", { count: "exact", head: true })
      .eq("user_id", user.id)
      .gt("created_at", prefs.last_viewed_expenses_at);

    return count || 0;
  };

  const fetchPendingContractsCount = async (): Promise<number> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;

    const { count } = await supabase
      .from("pending_contract_detections")
      .select("*", { count: "exact", head: true })
      .eq("user_id", user.id);

    return count || 0;
  };

  const fetchPendingContracts = async (): Promise<PendingContractDetection[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Fetch pending detections with erp_source from database (including business_central and poweroffice)
    const { data, error } = await supabase
      .from("pending_contract_detections")
      .select("id, supplier_name, average_amount, currency_code, confidence_score, detected_at, integration_id, erp_source, detection_type")
      .eq("user_id", user.id)
      .order("detected_at", { ascending: false })
      .limit(20);

    if (error) {
      console.error("Error fetching pending contracts", error);
      throw error;
    }

    if (!data || data.length === 0) return [];

    // Get unique integration IDs grouped by ERP source
    const tripletexIds = data.filter(d => d.erp_source === 'tripletex').map(d => d.integration_id);
    const finagoIds = data.filter(d => d.erp_source === 'finago').map(d => d.integration_id);
    const powerofficeIds = data.filter(d => d.erp_source === 'poweroffice').map(d => d.integration_id);
    const businessCentralIds = data.filter(d => d.erp_source === 'business_central').map(d => d.integration_id);

    // Fetch Tripletex integrations
    let tripletexIntegrations: { id: string; company_name: string | null }[] = [];
    if (tripletexIds.length > 0) {
      const uniqueTripletexIds = [...new Set(tripletexIds)];
      const { data, error } = await supabase
        .from("tripletex_integrations")
        .select("id, company_name")
        .in("id", uniqueTripletexIds);
      if (error) {
        console.error("Error fetching tripletex integrations:", error);
      } else {
        tripletexIntegrations = data || [];
      }
    }

    // Fetch Finago integrations
    let finagoIntegrations: { id: string; organization_name: string | null }[] = [];
    if (finagoIds.length > 0) {
      const uniqueFinagoIds = [...new Set(finagoIds)];
      const { data, error } = await supabase
        .from("finago_integrations")
        .select("id, organization_name")
        .in("id", uniqueFinagoIds);
      if (error) {
        console.error("Error fetching finago integrations:", error);
      } else {
        finagoIntegrations = data || [];
      }
    }

    // Fetch PowerOffice integrations
    let powerofficeIntegrations: { id: string; company_name: string | null }[] = [];
    if (powerofficeIds.length > 0) {
      const uniquePowerofficeIds = [...new Set(powerofficeIds)];
      const { data, error } = await supabase
        .from("poweroffice_integrations")
        .select("id, company_name")
        .in("id", uniquePowerofficeIds);
      if (error) {
        console.error("Error fetching poweroffice integrations:", error);
      } else {
        powerofficeIntegrations = data || [];
      }
    }

    // Fetch Business Central integrations
    let businessCentralIntegrations: { id: string; company_name: string | null }[] = [];
    if (businessCentralIds.length > 0) {
      const uniqueBcIds = [...new Set(businessCentralIds)];
      const { data, error } = await supabase
        .from("business_central_integrations")
        .select("id, company_name")
        .in("id", uniqueBcIds);
      if (error) {
        console.error("Error fetching business central integrations:", error);
      } else {
        businessCentralIntegrations = data || [];
      }
    }

    // Build lookup maps
    const tripletexMap = new Map(tripletexIntegrations.map(i => [i.id, i.company_name]));
    const finagoMap = new Map(finagoIntegrations.map(i => [i.id, i.organization_name]));
    const powerofficeMap = new Map(powerofficeIntegrations.map(i => [i.id, i.company_name]));
    const businessCentralMap = new Map(businessCentralIntegrations.map(i => [i.id, i.company_name]));

    return data.map(d => {
      const erpSource = (d.erp_source || 'tripletex') as 'tripletex' | 'finago' | 'poweroffice' | 'business_central';
      const detectionType = ((d as any).detection_type || 'expense') as 'expense' | 'sales';
      
      return {
        id: d.id,
        supplier_name: d.supplier_name,
        average_amount: d.average_amount,
        currency_code: d.currency_code,
        confidence_score: d.confidence_score,
        detected_at: new Date(d.detected_at),
        integration_id: d.integration_id,
        erp_source: erpSource,
        detection_type: detectionType,
        client_name: erpSource === 'tripletex'
          ? tripletexMap.get(d.integration_id) || null 
          : erpSource === 'finago'
          ? finagoMap.get(d.integration_id) || null
          : erpSource === 'poweroffice'
          ? powerofficeMap.get(d.integration_id) || null
          : businessCentralMap.get(d.integration_id) || null,
      };
    });
  };

  const fetchNewExpenses = async (): Promise<ExpenseNotification[]> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Get last viewed timestamp
    const { data: prefs } = await supabase
      .from("user_preferences")
      .select("last_viewed_expenses_at")
      .eq("user_id", user.id)
      .single();

    if (!prefs?.last_viewed_expenses_at) return [];

    // Fetch expenses created after last viewed
    const { data: expenses, error } = await supabase
      .from("contract_expense_timeline")
      .select("id, contract_id, amount, transaction_date, description, created_at")
      .eq("user_id", user.id)
      .gt("created_at", prefs.last_viewed_expenses_at)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error) {
      console.error("Error fetching new expenses", error);
      throw error;
    }

    if (!expenses || expenses.length === 0) return [];

    // Fetch supplier names for the related contracts
    const contractIds = Array.from(new Set(expenses.map((e: any) => e.contract_id)));
    const { data: contracts, error: contractsError } = await supabase
      .from("contracts")
      .select("id, supplier_name")
      .in("id", contractIds);

    if (contractsError) {
      console.error("Error fetching contracts for expenses", contractsError);
    }

    const supplierMap = new Map<string, string>();
    (contracts || []).forEach((c: any) => {
      supplierMap.set(c.id, c.supplier_name);
    });

    return (expenses || []).map((expense: any) => ({
      id: expense.id,
      contractId: expense.contract_id,
      supplierName: supplierMap.get(expense.contract_id) || "Unknown vendor",
      amount: expense.amount,
      transactionDate: new Date(expense.transaction_date),
      description: expense.description,
    }));
  };

  const markExpensesAsViewed = async (): Promise<void> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { error } = await supabase
      .from("user_preferences")
      .update({ last_viewed_expenses_at: new Date().toISOString() })
      .eq("user_id", user.id);

    if (error) throw error;
  };

  const useNewExpensesCountQuery = () => {
    return useQuery({
      queryKey: ["new-expenses-count"],
      queryFn: fetchNewExpensesCount,
      refetchInterval: 30000, // Refetch every 30 seconds
    });
  };

  const usePendingContractsCountQuery = () => {
    return useQuery({
      queryKey: ["pending-contracts-count"],
      queryFn: fetchPendingContractsCount,
      refetchInterval: 30000, // Refetch every 30 seconds
    });
  };

  const useNewExpensesQuery = () => {
    return useQuery({
      queryKey: ["new-expenses"],
      queryFn: fetchNewExpenses,
    });
  };

  const usePendingContractsQuery = () => {
    return useQuery({
      queryKey: ["pending-contracts"],
      queryFn: fetchPendingContracts,
    });
  };

  const useMarkExpensesViewedMutation = () => {
    return useMutation({
      mutationFn: markExpensesAsViewed,
      onSuccess: () => {
        // Only update the badge count, not the list itself
        // The list will refresh naturally when the popover is reopened
        queryClient.invalidateQueries({ queryKey: ["new-expenses-count"] });
      },
    });
  };

  return {
    useNewExpensesCountQuery,
    usePendingContractsCountQuery,
    useNewExpensesQuery,
    usePendingContractsQuery,
    useMarkExpensesViewedMutation,
  };
}